import { useEffect, useMemo, useState } from "react";

import { Controller, useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import dayjs from "dayjs";
import { ArrowRight, Loader2, Plus } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Textarea } from "~/components/ui/textarea";
import { getProductionUnitLabel } from "~/constants/units";
import {
  useBatchReadyPallets,
  useCreateProductTransfer,
  useProductLotesForTransfer,
  useProductUnit,
  useTransferFgBetweenPlants,
  useTransferFgPalletsBetweenPlants,
} from "~/hooks/purchasing";
import { useToast } from "~/hooks/use-toast";
import { useProfileStore } from "~/store/profileStore";
import { createProductTransferSchema } from "~/types/productTransfer";
import { PLANT_LABELS, TRANSFER_PLANT_OPTIONS } from "~/types/purchasing";

import { ProductSearchSelect } from "./ProductSearchSelect";

import type {
  CreateProductTransferFormData,
  ProductLoteAvailability,
  ProductLoteSource,
} from "~/types/productTransfer";
import type { Plant } from "~/types/purchasing";


interface ProductTransferFormProps {
  onDone: () => void;
}

// A lote (and origin) is either an ingested bodega lote or an un-ingested produced
// batch held at a plant. Bodega N and Planta N are the same physical place (P1≡B1,
// P2≡B2, NC≡NC), so the code alone can't tell them apart (NC collides) — the
// discriminator is the RPC's warehouse_id: bodega rows carry a uuid, plant rows null.
type TransferKind = "bodega" | "plant";

const fmtQty = (n: number) => n.toLocaleString("es-CL");

const loteKind = (l: ProductLoteAvailability): TransferKind =>
  l.warehouse_id == null ? "plant" : "bodega";

const otherPlant = (code: string) =>
  (TRANSFER_PLANT_OPTIONS.find((p) => p.value !== code)?.value ?? "") as string;

// Matching bodega code for a plant (same physical place): P1→B1, P2→B2, NC→NC.
const plantToBodega = (plant: string) =>
  plant === "NC" ? "NC" : `B${plant.slice(1)}`;

// Matching plant code for a bodega (same physical place): B1→P1, B2→P2, NC→NC.
// Pass-through for codes that are already plant codes, so it normalizes any origin
// code to the Planta vocabulary the form speaks.
const bodegaToPlant = (code: string) =>
  code.startsWith("B") ? `P${code.slice(1)}` : code;

// Stable, collision-free lote key (kind + code + lot + batch).
const loteKeyParts = (
  kind: TransferKind,
  code: string,
  lot: string | null,
  batch: string | null
) => `${kind}|${code}|${lot ?? "∅"}|${batch ?? "∅"}`;
const loteKey = (l: ProductLoteAvailability) =>
  loteKeyParts(loteKind(l), l.warehouse_code, l.lot_number, l.production_batch_id);

// Move a finished product between bodegas (B1 <-> B2 <-> NC) OR relocate an
// un-ingested produced batch between plants (P1 <-> P2 <-> NC). A transfer keeps the
// stock's state — ingested stays bodega->bodega, un-ingested stays plant->plant —
// so it never crosses plant <-> bodega (that would be an ingreso, handled elsewhere).
export function ProductTransferForm({ onDone }: ProductTransferFormProps) {
  const { toast } = useToast();
  const { id: userId } = useProfileStore();
  const createTransfer = useCreateProductTransfer();
  const fgPlantTransfer = useTransferFgBetweenPlants();
  const fgPalletTransfer = useTransferFgPalletsBetweenPlants();

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CreateProductTransferFormData>({
    resolver: zodResolver(createProductTransferSchema),
    defaultValues: {
      from_warehouse: "P1",
      to_warehouse: "P2",
      product_id: "",
      product_name: "",
      lot_number: null,
      production_batch_id: null,
      lote_label: "",
      available: 0,
      notes: "",
    },
  });

  const productId = watch("product_id");
  const productName = watch("product_name");
  const fromWarehouse = watch("from_warehouse");
  const toWarehouse = watch("to_warehouse");
  const loteLabel = watch("lote_label");
  const productionBatchId = watch("production_batch_id");
  const available = watch("available");
  const boxes = Number(watch("boxes")) || 0;

  // The origin is always a planta (Planta 1/2/NC). Each planta surfaces both its
  // un-ingested lotes AND its matching bodega's ingested lotes; the SELECTED lote's
  // kind is what decides the transfer domain. Before a lote is picked we assume plant.
  const [selectedKind, setSelectedKind] = useState<TransferKind | "">("");
  const [selectedFromCode, setSelectedFromCode] = useState("");
  // Which arm the picked lote came from: a still-open batch's finished pallets
  // ('plant_pallets') transfers by pallet checklist; everything else by quantity.
  const [selectedSource, setSelectedSource] = useState<ProductLoteSource | "">(
    ""
  );
  const [selectedPalletIds, setSelectedPalletIds] = useState<string[]>([]);
  const effectiveKind: TransferKind = selectedKind || "plant";
  const effectiveIsPlant = effectiveKind === "plant";
  // Real origin code for the RPC: a bodega lote carries its B-code, everything else
  // the origin planta. Display always uses the origin planta label (same site).
  const effectiveFromCode = selectedFromCode || fromWarehouse;
  const originPlantLabel = PLANT_LABELS[fromWarehouse as Plant] ?? fromWarehouse;
  const isPalletTransfer = selectedSource === "plant_pallets";

  const { data: lotes = [], isLoading: lotesLoading } =
    useProductLotesForTransfer(productId || null);

  // Finished ("Listo") pallets of the selected open lote — the transfer checklist.
  const { data: readyPallets = [], isLoading: palletsLoading } =
    useBatchReadyPallets(
      isPalletTransfer ? productionBatchId : null,
      isPalletTransfer ? effectiveFromCode : null
    );

  // Label quantities in the product's own unit (Cajas / Unidades / Litros / …)
  // instead of always "cajas". The WMS count is already stored in this unit.
  const { data: unitCode } = useProductUnit(productId || null);
  const unitPlural = getProductionUnitLabel(unitCode, 2);
  const unitFor = (n: number) => getProductionUnitLabel(unitCode, n);

  // Lotes visible for the chosen origin planta: its un-ingested lotes PLUS the
  // matching bodega's ingested lotes (both are physically at that planta).
  const lotesInOrigin = useMemo(
    () =>
      lotes.filter((l) => {
        const lk = loteKind(l);
        return (
          (lk === "plant" && l.warehouse_code === fromWarehouse) ||
          (lk === "bodega" && l.warehouse_code === plantToBodega(fromWarehouse))
        );
      }),
    [lotes, fromWarehouse]
  );

  // Default destination planta for a given origin code (bodega or plant), normalized
  // to Planta vocabulary. The destination is always the other planta.
  const defaultDest = (code: string): string => otherPlant(bodegaToPlant(code));

  const resetLote = () => {
    setSelectedKind("");
    setSelectedFromCode("");
    setSelectedSource("");
    setSelectedPalletIds([]);
    setValue("lote_label", "");
    setValue("lot_number", null);
    setValue("production_batch_id", null);
    setValue("available", 0);
    setValue("boxes", 0);
  };

  // When lotes load for a product, default the origin planta to the one holding the
  // most available stock, pair the other planta as destination, and clear any lote.
  useEffect(() => {
    if (!productId || lotes.length === 0) return;
    const best = [...lotes].sort(
      (a, b) => b.available_boxes - a.available_boxes
    )[0];
    const visible = lotes.some(
      (l) =>
        (loteKind(l) === "plant" && l.warehouse_code === fromWarehouse) ||
        (loteKind(l) === "bodega" &&
          l.warehouse_code === plantToBodega(fromWarehouse))
    );
    if (best && !visible) {
      setValue("from_warehouse", bodegaToPlant(best.warehouse_code));
      setValue("to_warehouse", defaultDest(best.warehouse_code));
    }
    resetLote();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lotes, productId]);

  const handleSelectProduct = (p: { id: string; name: string }) => {
    setValue("product_id", p.id, { shouldValidate: true });
    setValue("product_name", p.name);
    resetLote();
  };

  // Origin is a planta code (P1/P2/NC); the destination defaults to the other planta.
  const handleSelectOrigin = (code: string) => {
    setValue("from_warehouse", code);
    setValue("to_warehouse", defaultDest(code));
    resetLote();
  };

  const handleSelectLote = (key: string) => {
    const lote = lotesInOrigin.find((l) => loteKey(l) === key);
    if (!lote) return;
    const kind = loteKind(lote);
    setSelectedKind(kind);
    setSelectedFromCode(lote.warehouse_code);
    setSelectedSource(lote.source);
    setSelectedPalletIds([]);
    setValue("lot_number", lote.lot_number);
    setValue("production_batch_id", lote.production_batch_id);
    setValue("lote_label", lote.lote_label);
    setValue("available", lote.available_boxes, { shouldValidate: true });
    setValue("boxes", 0);
    // The lote's location + state determines the transfer domain (bodega vs plant),
    // but the destination is always expressed as the other planta.
    setValue("to_warehouse", defaultDest(lote.warehouse_code));
  };

  // Toggle a finished pallet in/out of the transfer; the quantity is the sum of the
  // selected pallets' boxes (un-ingested pallets move whole, never split).
  const togglePallet = (id: string) => {
    setSelectedPalletIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id];
      const sum = readyPallets
        .filter((p) => next.includes(p.id))
        .reduce((s, p) => s + p.current_box_count, 0);
      setValue("boxes", sum, { shouldValidate: true });
      return next;
    });
  };

  const canSubmit =
    !!productId &&
    loteLabel !== "" &&
    boxes > 0 &&
    boxes <= available &&
    // Origin and destination plantas must differ (both are plant codes).
    fromWarehouse !== toWarehouse &&
    // Plant transfers always move a specific produced batch.
    (!effectiveIsPlant || !!productionBatchId);

  const isPending =
    createTransfer.isPending ||
    fgPlantTransfer.isPending ||
    fgPalletTransfer.isPending;

  const onSubmit = (data: CreateProductTransferFormData) => {
    if (!userId) {
      toast({
        title: "Error",
        description: "No se pudo identificar al usuario.",
        variant: "destructive",
      });
      return;
    }

    const notes = data.notes?.trim() ? data.notes.trim() : null;

    // Plant -> plant, STILL-OPEN batch: relocate whole finished ("Listo") pallets
    // as production advances (partial + repeatable), no need to wait for close.
    if (effectiveIsPlant && isPalletTransfer) {
      if (!data.production_batch_id || selectedPalletIds.length === 0) {
        toast({
          title: "Error",
          description: "Selecciona al menos un pallet.",
          variant: "destructive",
        });
        return;
      }
      fgPalletTransfer.mutate(
        {
          productionBatchId: data.production_batch_id,
          fromPlant: effectiveFromCode,
          toPlant: data.to_warehouse,
          palletIds: selectedPalletIds,
          notes,
          performedBy: userId,
        },
        {
          onSuccess: (res) => {
            toast({
              title: "Traslado registrado",
              description: `${res.transfer_number}: ${res.pallets} pallet(s) · ${fmtQty(
                res.boxes
              )} ${unitFor(res.boxes)} del lote ${data.lote_label} de ${
                PLANT_LABELS[effectiveFromCode as Plant]
              } a ${PLANT_LABELS[data.to_warehouse as Plant]}.`,
            });
            reset();
            resetLote();
            onDone();
          },
          onError: (error) => {
            toast({
              title: "Error",
              description: error.message,
              variant: "destructive",
            });
          },
        }
      );
      return;
    }

    // Plant -> plant, COMPLETED batch: relocate un-ingested pending quantity (boxes).
    if (effectiveIsPlant) {
      if (!data.production_batch_id) {
        toast({
          title: "Error",
          description: "Selecciona un lote de producción.",
          variant: "destructive",
        });
        return;
      }
      fgPlantTransfer.mutate(
        {
          productionBatchId: data.production_batch_id,
          fromPlant: effectiveFromCode,
          toPlant: data.to_warehouse,
          boxes: data.boxes,
          notes,
          performedBy: userId,
        },
        {
          onSuccess: (res) => {
            toast({
              title: "Traslado registrado",
              description: `${res.transfer_number}: ${fmtQty(data.boxes)} ${unitFor(
                data.boxes
              )} del lote ${data.lote_label} de ${
                PLANT_LABELS[effectiveFromCode as Plant]
              } a ${PLANT_LABELS[data.to_warehouse as Plant]}.`,
            });
            reset();
            resetLote();
            onDone();
          },
          onError: (error) => {
            toast({
              title: "Error",
              description: error.message,
              variant: "destructive",
            });
          },
        }
      );
      return;
    }

    // Ingested lote: WMS relocation between bodegas (leaves origin into transit). The
    // origin bodega is the selected lote's code; the destination planta maps 1:1 to
    // its bodega (P→B).
    createTransfer.mutate(
      {
        fromWarehouse: effectiveFromCode,
        toWarehouse: plantToBodega(data.to_warehouse),
        productId: data.product_id,
        boxes: data.boxes,
        lotNumber: data.lot_number,
        productionBatchId: data.production_batch_id,
        notes,
        performedBy: userId,
      },
      {
        onSuccess: (res) => {
          toast({
            title: "Traslado enviado",
            description: `Guía ${res.transfer_number} enviada a ${
              PLANT_LABELS[data.to_warehouse as Plant] ?? data.to_warehouse
            }. Pendiente de recepción.`,
          });
          reset();
          resetLote();
          onDone();
        },
        onError: (error) => {
          toast({
            title: "Error",
            description: error.message,
            variant: "destructive",
          });
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Traslada producto terminado entre plantas (Planta 1, Planta 2, No
        Conforme). Se listan los lotes disponibles en la planta de origen, ya
        estén <span className="font-medium">ingresados a bodega</span> o aún en
        planta.
      </p>

      {/* Product */}
      <div className="space-y-1.5">
        <Label>Producto</Label>
        <ProductSearchSelect
          value={productId}
          selectedLabel={productName}
          onSelect={handleSelectProduct}
        />
        {errors.product_id && (
          <p className="text-xs text-red-700">{errors.product_id.message}</p>
        )}
      </div>

      {/* Origen / Destino */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1 space-y-1.5">
          <Label>Origen</Label>
          <Select value={fromWarehouse} onValueChange={handleSelectOrigin}>
            <SelectTrigger className="h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TRANSFER_PLANT_OPTIONS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <ArrowRight className="mx-auto hidden h-4 w-4 shrink-0 text-muted-foreground sm:mb-2.5 sm:block" />
        <div className="flex-1 space-y-1.5">
          <Label>Destino</Label>
          <Controller
            control={control}
            name="to_warehouse"
            render={({ field }) => (
              <Select value={field.value} onValueChange={field.onChange}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TRANSFER_PLANT_OPTIONS.filter(
                    (p) => p.value !== fromWarehouse
                  ).map((p) => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </div>
      </div>
      {errors.to_warehouse?.message && (
        <p className="text-xs text-red-700">{errors.to_warehouse.message}</p>
      )}

      {/* Lote */}
      <div className="space-y-1.5">
        <Label>Lote</Label>
        <Select
          value={
            loteLabel
              ? loteKeyParts(
                  effectiveKind,
                  effectiveFromCode,
                  watch("lot_number"),
                  watch("production_batch_id")
                )
              : ""
          }
          onValueChange={handleSelectLote}
          disabled={!productId || lotesLoading}
        >
          <SelectTrigger className="h-9">
            <SelectValue
              placeholder={
                !productId
                  ? "Primero elige un producto"
                  : lotesLoading
                    ? "Cargando lotes…"
                    : lotesInOrigin.length === 0
                      ? `Sin lotes en ${originPlantLabel}`
                      : "Selecciona un lote"
              }
            />
          </SelectTrigger>
          <SelectContent>
            {lotesInOrigin.map((l) => (
              <SelectItem key={loteKey(l)} value={loteKey(l)}>
                {l.lote_label} — {fmtQty(l.available_boxes)}{" "}
                {unitFor(l.available_boxes)}
                {` · ${
                  loteKind(l) === "plant"
                    ? l.source === "plant_pallets"
                      ? "en producción"
                      : "no ingresado"
                    : "ingresado"
                }`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Cantidad — a pallet checklist for a still-open lote, else a quantity input */}
      {isPalletTransfer ? (
        <div className="space-y-1.5">
          <Label>Pallets a trasladar</Label>
          {palletsLoading ? (
            <div className="flex items-center gap-2 rounded-md border px-3 py-3 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando pallets…
            </div>
          ) : readyPallets.length === 0 ? (
            <p className="rounded-md border px-3 py-3 text-sm text-muted-foreground">
              No hay pallets listos en {originPlantLabel}.
            </p>
          ) : (
            <div className="max-h-56 space-y-0.5 overflow-y-auto rounded-md border p-1">
              {readyPallets.map((p) => (
                <label
                  key={p.id}
                  className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-2 hover:bg-muted/40"
                >
                  <Checkbox
                    checked={selectedPalletIds.includes(p.id)}
                    onCheckedChange={() => togglePallet(p.id)}
                  />
                  <div className="flex-1 text-sm">
                    <span className="font-medium">
                      Pallet #{p.sequence_number ?? "-"}
                    </span>
                    <span className="text-muted-foreground">
                      {" "}
                      · {fmtQty(p.current_box_count)}{" "}
                      {unitFor(p.current_box_count)}
                    </span>
                  </div>
                  {p.production_ended_at && (
                    <span className="text-xs text-muted-foreground">
                      {dayjs(p.production_ended_at).format("HH:mm")}
                    </span>
                  )}
                </label>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Seleccionados: {fmtQty(boxes)} {unitFor(boxes)} ·{" "}
            {selectedPalletIds.length} de {readyPallets.length} pallets ·
            Disponible {fmtQty(available)} {unitFor(available)}
          </p>
        </div>
      ) : (
        <div className="space-y-1.5">
          <Label htmlFor="pt-boxes">Cantidad ({unitPlural})</Label>
          <Input
            id="pt-boxes"
            type="number"
            step="1"
            min="1"
            className="h-9"
            placeholder={`Cantidad de ${unitPlural}`}
            disabled={loteLabel === ""}
            {...register("boxes", { valueAsNumber: true })}
          />
          {loteLabel !== "" && (
            <p className="text-xs text-muted-foreground">
              Disponible en {originPlantLabel}: {fmtQty(available)}{" "}
              {unitFor(available)}
            </p>
          )}
          {errors.boxes?.message && (
            <p className="text-xs text-red-700">{errors.boxes.message}</p>
          )}
          {loteLabel !== "" && boxes > available && (
            <p className="text-xs text-red-700">
              Excede lo disponible en {originPlantLabel} ({fmtQty(available)}{" "}
              {unitFor(available)}).
            </p>
          )}
        </div>
      )}

      {/* Notes */}
      <div className="space-y-1.5">
        <Label htmlFor="pt-transfer-notes">Notas (opcional)</Label>
        <Textarea
          id="pt-transfer-notes"
          rows={2}
          placeholder="Motivo o referencia del traslado"
          {...register("notes")}
        />
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onDone}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isPending || !canSubmit}>
          {isPending ? (
            "Procesando…"
          ) : (
            <>
              <Plus className="mr-1 h-4 w-4" />
              {effectiveIsPlant ? "Registrar traslado" : "Enviar traslado"}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
